import React from 'react';
import {
  withRouter,
  Switch,
} from 'react-router-dom';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';
import {
  withStyles,
  withMobileDialog,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import {
  requestTenant,
  getTenantUsers,
} from '@pingum/app-state-management/tenant/actions';

// utils
import { getTenantFromUrl } from '../../utils/urlHelper';
import { isMenuShownOnCurrentPage } from '../../utils/menu';
import styles from '../../utils/styles';
import { isPublicPage } from '../../utils/tenantAuthenticationHelper';

// navigation
import Drawer from '../navigation/Drawer';
import Route from '../navigation/Route';
import PrivateRoute from '../navigation/PrivateRoute';

// widgets
import ErrorContainer from '../widgets/ErrorContainer';
import SuccessContainer from '../widgets/SuccessContainer';

// modals
import SupportModal from '../modals/SupportModal';
import AuditLogsModal from '../modals/AuditLogsModal';
import LoadingPage from '../pages/LoadingPage';

// reducer actions
import {
  getMe,
  getMyTenants,
  getMySeenTutorials,
} from '../../redux/user/actions';

import './styles.scss';

export class AppComponent extends React.Component {
  constructor(props) {
    super(props);

    if (props.auth.isAuthenticated
      && props.history.location.pathname !== '/logout'
      && !isPublicPage()) {
      props.getMe();
      props.getMyTenants();
      props.getMySeenTutorials();
    }

    if (env.REACT_APP_GOOGLE_ANALYTICS_ID) {
      ReactGA.initialize(env.REACT_APP_GOOGLE_ANALYTICS_ID);
      ReactGA.pageview(props.history.location.pathname);
      props.history.listen((location) => {
        ReactGA.pageview(location.pathname);
      });
    }
  }

  render() {
    const tenant = getTenantFromUrl();
    const {
      isLoading,
      drawerOpen,
      classes,
      fullScreen,
      pages: {
        TenantBilling,
        OrganizationSubscriptions,
        TenantSettings,
        EventSettings,
        EventTemplateSettings,
        EventTemplateDetailSettings,
        FormSettings,
        DashboardPage,
        HomePage,
        DashboardSettings,
        MyAccount,
        Developers,
        NotFoundPage,
        UserOnboarding,
        LoginPage,
        LogoutPage,
        ConfirmEmailPage,
        AccessDeniedPage,
        InvalidPaymentPage,
        DeactivatedOrganizationPage,
        EventsApi,
        NotificationSettingsPage,
        UserSubscriptionsPage,
        EventDetailPage,
        TenantSelectionPage,
        FormPage,
      },
    } = this.props;
    const showMenu = isMenuShownOnCurrentPage(this.props.history.location.pathname);

    if (tenant && !isPublicPage()) {
      this.props.getTenant(tenant);
      this.props.getTenantUsers(tenant);
    }

    let contentClass = classes.contentWithoutMenu;
    if (!fullScreen && showMenu && drawerOpen) {
      contentClass = classes.content;
    }
    if (!fullScreen && showMenu && !drawerOpen) {
      contentClass = classes.contentDrawerClosed;
    }

    return (
      <div className={this.props.classes.root}>

        <Helmet
          script={
            env.REACT_APP_GOOGLE_TAG_MANAGER_ID
              ? [{ innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer', '${env.REACT_APP_GOOGLE_TAG_MANAGER_ID}');` }]
              : []
          }
        />
        {
          showMenu
            && <Drawer />
        }
        <ErrorContainer />
        <SuccessContainer />
        <main
          role="main"
          className={contentClass}
        >

          {
            tenant
              && isPublicPage()
              && (
                <Switch>
                  <Route exact path="/forms/:id" component={FormPage} title="Pingum Forms" />
                  <Route component={NotFoundPage} title="Page not found" />
                </Switch>
              )
          }
          {
            isLoading
              && !isPublicPage()
              && (
                <Switch>
                  <Route exact path="/callback" component={LoadingPage} title="Login" />
                  <Route exact path="/confirm-email" component={ConfirmEmailPage} title="Please confirm your email address" />
                  <Route exact path="/login" component={LoginPage} title="Login" />
                  <Route exact path="/logout" component={LogoutPage} title="Logged out" />
                  <Route exact path="/access-denied" component={AccessDeniedPage} title="Access Denied" />
                  <Route exact path="/deactivated-organization" component={DeactivatedOrganizationPage} title="Deactivated Account" />
                  <Route exact path="/invalid-payment" component={InvalidPaymentPage} title="Invalid Payment" />
                  <Route component={LoadingPage} />
                </Switch>
              )
          }
          {
            !isLoading
              && !tenant
              && !isPublicPage()
              && (
                <Switch>
                  <Route exact path="/" component={TenantSelectionPage} title="Notifications made easy" />
                  <Route exact path="/docs/api" component={EventsApi} title="API Documentation" />
                  <PrivateRoute exact path="/organizations" component={TenantSelectionPage} title="Select your organization" />
                  <Route exact path="/callback" component={LoadingPage} title="Login" />
                  <Route exact path="/confirm-email" component={ConfirmEmailPage} title="Please confirm your email address" />
                  <Route exact path="/login" component={LoginPage} title="Login" />
                  <Route exact path="/logout" component={LogoutPage} title="Logout" />
                  <Route exact path="/access-denied" component={AccessDeniedPage} title="Access Denied" />
                  <Route exact path="/deactivated-organization" component={DeactivatedOrganizationPage} title="Deactivated Account" />
                  <Route exact path="/invalid-payment" component={InvalidPaymentPage} title="Invalid Payment" />
                  <Route component={NotFoundPage} title="Page not found" />
                </Switch>
              )
          }
          {
            !isLoading
              && tenant
              && !isPublicPage()
              && (
                <Switch>
                  <PrivateRoute exact path="/" component={HomePage} title="Home" />
                  <PrivateRoute exact path="/my-account" component={MyAccount} title="My Account" />
                  <PrivateRoute exact path="/my-subscriptions" component={UserSubscriptionsPage} title="My Subscriptions" />
                  <PrivateRoute exact path="/user-onboarding" component={UserOnboarding} title="Welcome" />
                  <PrivateRoute exact path="/dashboards" component={DashboardPage} title="Dashboards" />
                  <PrivateRoute exact path="/home" component={HomePage} title="Home" />
                  <PrivateRoute exact path="/dashboards/:directLinkName" component={DashboardPage} title="Dashboards" />
                  <PrivateRoute exact path="/dashboards/workflows/:id" component={DashboardPage} title="Dashboards" />
                  <PrivateRoute exact path="/settings/my-dashboards" component={DashboardSettings} title="Dashboard Settings" />
                  <PrivateRoute exact path="/settings/organization" component={TenantSettings} title="Organization Settings" />
                  <PrivateRoute exact path="/settings/billing" component={TenantBilling} title="Billing" />
                  <PrivateRoute exact path="/settings/subscriptions" component={OrganizationSubscriptions} title="Organization Subscriptions" />
                  <PrivateRoute exact path="/settings/events" component={EventSettings} title="Event Settings" />
                  <PrivateRoute exact path="/settings/workflows" component={EventTemplateSettings} title="Workflow Settings" />
                  <PrivateRoute exact path="/settings/workflows/new" component={EventTemplateDetailSettings} title="Workflow Settings" />
                  <PrivateRoute exact path="/settings/workflows/:id" component={EventTemplateDetailSettings} title="Workflow Settings" />
                  <PrivateRoute exact path="/settings/forms" component={FormSettings} title="Form Settings" />
                  <PrivateRoute exact path="/developers" component={Developers} title="Developers" />
                  <PrivateRoute exact path="/settings/notifications" component={NotificationSettingsPage} title="Notification Settings" />
                  <PrivateRoute exact path="/events/:id" component={EventDetailPage} title="Event Details" />
                  <Route exact path="/docs/api" component={EventsApi} title="API Documentation" />
                  <Route exact path="/callback" component={LoadingPage} title="Loading" />
                  <Route exact path="/confirm-email" component={ConfirmEmailPage} title="Please confirm your email address" />
                  <Route exact path="/login" component={LoginPage} title="Login" />
                  <Route exact path="/logout" component={LogoutPage} title="Logout" />
                  <Route exact path="/access-denied" component={AccessDeniedPage} title="Access Denied" />
                  <Route exact path="/deactivated-organization" component={DeactivatedOrganizationPage} title="Deactivated Account" />
                  <Route exact path="/invalid-payment" component={InvalidPaymentPage} title="Invalid Payment" />
                  <Route component={NotFoundPage} title="Page not found" />
                </Switch>
              )
          }
          <SupportModal />
          <AuditLogsModal />
        </main>
      </div>
    );
  }
}

AppComponent.propTypes = {
  auth: PropTypes.shape({
    isAuthenticated: PropTypes.bool.isRequired,
    error: PropTypes.object,
    isFetching: PropTypes.bool,
  }).isRequired,
  classes: PropTypes.object,
  getMe: PropTypes.func.isRequired,
  getMyTenants: PropTypes.func.isRequired,
  getTenant: PropTypes.func.isRequired,
  getTenantUsers: PropTypes.func,
  history: PropTypes.shape({
    location: PropTypes.shape({ pathname: PropTypes.string }),
    listen: PropTypes.func,
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getMySeenTutorials: PropTypes.func.isRequired,
  drawerOpen: PropTypes.bool,
  fullScreen: PropTypes.bool,
  pages: PropTypes.object,
};

AppComponent.defaultProps = {
  getTenantUsers: () => {},
  drawerOpen: false,
  fullScreen: true,
  classes: {},
  pages: {
    TenantBilling: React.lazy(() => import('../pages/TenantBilling')),
    OrganizationSubscriptions: React.lazy(() => import('../pages/OrganizationSubscriptions')),
    TenantSettings: React.lazy(() => import('../pages/TenantSettings')),
    EventSettings: React.lazy(() => import('../pages/EventSettings')),
    FormSettings: React.lazy(() => import('../pages/FormSettings')),
    EventTemplateSettings: React.lazy(() => import('../pages/EventTemplateSettings')),
    EventTemplateDetailSettings: React.lazy(() => import('../pages/EventTemplateDetailSettings')),
    DashboardPage: React.lazy(() => import('../pages/DashboardPage')),
    HomePage: React.lazy(() => import('../pages/HomePage')),
    DashboardSettings: React.lazy(() => import('../pages/DashboardSettings')),
    MyAccount: React.lazy(() => import('../pages/MyAccount')),
    Developers: React.lazy(() => import('../pages/Developers')),
    NotFoundPage: React.lazy(() => import('../pages/NotFoundPage')),
    UserOnboarding: React.lazy(() => import('../tutorials/UserOnboarding')),
    LoginPage: React.lazy(() => import('../pages/LoginPage')),
    LogoutPage: React.lazy(() => import('../pages/LogoutPage')),
    ConfirmEmailPage: React.lazy(() => import('../pages/ConfirmEmailPage')),
    AccessDeniedPage: React.lazy(() => import('../pages/AccessDeniedPage')),
    DeactivatedOrganizationPage: React.lazy(() => import('../pages/DeactivatedOrganizationPage')),
    InvalidPaymentPage: React.lazy(() => import('../pages/InvalidPaymentPage')),
    EventsApi: React.lazy(() => import('../pages/Docs/EventsApi')),
    NotificationSettingsPage: React.lazy(() => import('../pages/NotificationSettings')),
    UserSubscriptionsPage: React.lazy(() => import('../pages/UserSubscriptions')),
    EventDetailPage: React.lazy(() => import('../pages/EventDetailPage')),
    TenantSelectionPage: React.lazy(() => import('../pages/TenantSelectionPage')),
    FormPage: React.lazy(() => import('../pages/FormPage')),
  },
};

export const mapStateToProps = (state) => ({
  auth: state.auth,
  isLoading: (!!state.user.isFetchingUser || !!state.user.isFetchingUserTenants),
  drawerOpen: state.app.drawerOpen,
});

export const mapDispatchToProps = (dispatch) => ({
  getMe: () => dispatch(getMe()),
  getMyTenants: () => dispatch(getMyTenants()),
  getTenant: (tenantName) => dispatch(requestTenant(tenantName)),
  getMySeenTutorials: () => dispatch(getMySeenTutorials()),
  getTenantUsers: (name) => dispatch(getTenantUsers(name)),
});

export default withRouter(withStyles(styles, { withTheme: true })(connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'xs' })(AppComponent))));
